<template>
	<div>
		<div class="menu-body-overlay" :class="{ active: showMenu }" @click="hideMenu" />

		<nav class="top-nav change-on-scroll" :class="{ subpage: !route.name.startsWith('index') }">
			<div class="row">
				<div class="columns column12">
					<div class="border change-on-scroll" />
					<div class="text-nav">
						<nuxt-link
							:to="{ path: localePath('/', locale), hash: route.name.startsWith('index') ? '#top' : '' }"
							class="logo change-on-scroll"
						/>
						<div class="menu-buttons change-on-scroll">
							<div
								v-if="defaults[locale].website.showBookButtons"
								class="dropdown-container"
								v-click-outside="closeDropdown"
							>
								<div class="dropdown-toggle button book-button" @click="toggleDropdown">
									<span>{{ $t('book') }}</span>
								</div>
								<div class="dropdown-menu" :class="{ open: showDropdown }">
									<a
										class="button book-button nav-buttons reserve-now namastay-offer-button"
										target="_self"
										:href="bookingUrl()"
									>
										<span class="button-mobile">{{ $t('bookARoom') }}</span>
									</a>
									<button
										type="button"
										class="book-button button nav-buttons reserve-now"
										@click="openGuestplan"
									>
										<span class="button-mobile">{{ $t('bookATable') }}</span>
									</button>
								</div>
							</div>
							<slot name="menu-button">
								<div class="menu-button nav-buttons" @click.prevent="openMenu">
									<div class="bar" />
									<div class="bar" />
									<div class="bar" />
								</div>
							</slot>
						</div>
					</div>
					<div class="fullscreen-nav" :class="{ active: showMenu }">
						<div class="menu-section">
							<div class="menu-close" @click="hideMenu">
								<span class="close-icon" />
							</div>
							<ul class="main-nav">
								<li v-for="item in topmenu[locale]" :key="item.filename">
									<nuxt-link
										:exact="item.slug === 'index'"
										:to="`${item.filename}`"
										@click="hideMenu"
									>
										{{ item.header }}
									</nuxt-link>
								</li>
							</ul>
							<social-media theme="dark" :socials="socials" />
							<div class="fullscreen-nav-footer">
								<div class="nav-footer-menu">
									<nuxt-link
										v-for="item in footermenu[locale]"
										:key="item.filename"
										:to="item.filename"
										@click="hideMenu"
									>
										{{ item.header }}
									</nuxt-link>
								</div>
								<lang-select :items="page.langmenu" @hide-menu="hideMenu" />
							</div>
						</div>
					</div>
				</div>
			</div>
		</nav>
		<header v-if="!(page && page.headingImages && page.headingImages.length)" class="no-slider" />
	</div>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, socials, fetchDefaults } = useDefaults();
const { locale } = useI18n();
const localePath = useLocalePath();
const route = useRoute();
const { bookingUrl } = useNamastay();

await fetchPage();
await fetchDefaults();

const showMenu = ref(false);
const showDropdown = ref(false);

const { data: footermenu } = await useWebsiteFetch('footermenu');
const { data: topmenu } = await useWebsiteFetch('topmenu');

const handleScroll = () => {
	const fixedElements = document.querySelectorAll('.fix-on-scroll');
	const colorChange = document.querySelectorAll('.change-on-scroll');
	if (window.scrollY >= 50) {
		fixedElements.forEach((el) => el.classList.add('fixed'));
	} else {
		fixedElements.forEach((el) => el.classList.remove('fixed'));
	}
	if (window.scrollY >= 610) {
		colorChange.forEach((el) => el.classList.add('dark'));
	} else {
		colorChange.forEach((el) => el.classList.remove('dark'));
	}
};

const openMenu = () => {
	showMenu.value = true;
	document.body.classList.add('locked');
};

const hideMenu = () => {
	showMenu.value = false;
	document.body.classList.remove('locked');
};

const toggleDropdown = () => {
	showDropdown.value = !showDropdown.value;
};

const closeDropdown = () => {
	showDropdown.value = false;
};

const openGuestplan = () => {
	if (typeof window === 'undefined') {
		return;
	}

	const gp = window._gstpln;
	if (!gp) {
		return;
	}

	gp.open = true;

	if (typeof gp.openWidget === 'function') {
		gp.openWidget();
	}
};

onMounted(() => {
	window.addEventListener('scroll', handleScroll);
});

onBeforeUnmount(() => {
	window.removeEventListener('scroll', handleScroll);
});
</script>

<style lang="scss" scoped>
header.no-slider {
	width: 100%;
	height: 125px;
}

.locked .top-nav {
	z-index: 101;
}

.button-mobile {
	vertical-align: middle;
}

.border {
	background: #f7f5eb;
	width: 100%;
	height: 1px;
	margin: 0 0 20px;
	transition: all 0.3s ease-in-out;

	&.dark {
		background: #000;
	}
}

.top-nav {
	position: fixed;
	top: 0;
	right: 0;
	left: 0;
	z-index: 18;
	padding: 20px 0;
	transition: background 0.3s ease-in-out;

	&.dark {
		background: #f7f5eb;
		padding: 10px 0;

		.border {
			height: 0;
			margin: 0;
		}
	}

	&.subpage {
		background: #f7f5eb;
		padding: 10px 0;

		.logo {
			background: url('~/assets/images/logo-icon-only-black.svg') center center no-repeat;
			background-size: 50px;
		}
	}

	.row {
		overflow: visible;
		max-width: 100vw;
	}

	.logo {
		background: url('~/assets/images/logo-icon-only-white.svg') center center no-repeat;
		background-size: 50px;
		width: 51px;
		height: 82px;
		float: left;
		z-index: 5;
		position: relative;

		&.dark {
			background: url('~/assets/images/logo-icon-only-black.svg') center center no-repeat;
			background-size: 50px;
		}
	}
}

.menu-body-overlay {
	display: none;
	position: fixed;
	inset: 0;
	z-index: 99;

	&.active {
		display: block;
	}
}

.menu-buttons {
	display: flex;
	flex-flow: row nowrap;
	gap: 20px;
}

.menu-close {
	display: flex;
	flex-flow: row wrap;
	justify-content: flex-end;
	padding-top: 22px;
	cursor: pointer;

	.close-icon {
		background: url('~/assets/images/menu-close-icon.png') center center no-repeat;
		background-size: 30px;
		width: 30px;
		height: 30px;
		display: block;
	}
}

.text-nav {
	z-index: 1;
	position: relative;
	display: flex;
	flex-flow: row nowrap;
	justify-content: space-between;
	align-items: center;
}

.book-button {
	color: #fff;
	background: $primary-color;
	cursor: pointer;
	font-family: 'courier_newregular', MessinaSerif, serif;
	line-height: 20px;
	padding: 14px 25px 7px;
	text-decoration: none;
	font-weight: 700;

	&:hover {
		background: $primary-color-hover;
	}
}

.nav-button-title {
	position: absolute;
	right: 100%;
	width: 0;
	opacity: 0;
	color: #000;
	font-size: $secondary-font-size;
}

.nav-buttons:hover .nav-button-title {
	opacity: 1;
	width: auto;
	white-space: nowrap;
	margin-right: 10px;
	transition: all 0.4s ease-out;
}

.menu-button {
	display: flex;
	flex-flow: column wrap;
	margin: 4px 0 0 10px;
	cursor: pointer;

	.bar {
		width: 44px;
		height: 1px;
		background: #f7f5eb;
		transition: all 0.3s ease-in-out;

		&:first-child {
			margin: 0 0 15px;
		}

		&:last-child {
			margin: 15px 0 0;
		}
	}
}

.subpage {
	.border,
	.bar {
		background: #000;
	}
}

.dark {
	.bar {
		background: #000;
	}
}

.fullscreen-nav {
	position: fixed;
	top: 0;
	right: 0;
	background: $footer-background-color;
	z-index: 100;
	width: 100%;
	max-width: 700px;
	height: 100%;
	opacity: 0;
	padding: 0 40px 40px;
	transition: all 0.5s ease-in-out;
	transform: translate(100%, 0%) matrix(1, 0, 0, 1, 0, 0);

	&.active {
		transform: matrix(1, 0, 0, 1, 0, 0);
		opacity: 1;
	}

	.socials {
		width: 100%;
		margin: 30px 0 0;

		> a {
			font-size: 22px;
		}
	}

	.main-nav {
		text-align: left;
		position: relative;
		width: 100%;

		li {
			display: block;
			font-size: 30px;
			line-height: 44px;
			margin: 4px 0;
			text-transform: none;
		}

		a {
			color: #fff;
			position: relative;
			padding: 0 0 2px;
			text-decoration: none;
			font-size: 30px;

			&:hover,
			&.active {
				color: $primary-color;
			}
		}
	}
}

.fullscreen-nav-footer {
	position: absolute;
	bottom: 60px;
	left: 40px;
	right: 40px;
	font-family: 'courier_newregular', MessinaSerif, serif;
	font-size: 14px;
	display: flex;
	flex-flow: row wrap;
	justify-content: space-between;
	align-items: center;

	.nav-footer-menu {
		display: flex;
		flex-flow: row wrap;

		a {
			margin: 0 25px 0 0;
			color: #fff;
			text-decoration: none;

			&:hover,
			&.active {
				color: $primary-color;
			}
		}
	}
}

.cta-fullscreen-menu {
	float: left;
	display: block;
}

.dropdown-container {
	position: relative;
	display: flex;
	flex-flow: column wrap;
	text-align: center;
	min-width: 160px;
}

.dropdown-menu {
	position: absolute;
	top: 100%;
	left: 0;
	width: 100%;
	display: flex;
	flex-flow: column wrap;
	align-items: stretch;
	justify-content: center;
	text-align: center;
	max-height: 0;
	overflow: hidden;
	opacity: 0;
	transition:
		max-height 0.4s ease-in-out,
		opacity 0.3s ease-in-out;

	&.open {
		max-height: 300px;
		opacity: 1;
	}

	.button {
		border: none;
		outline: none;
		transition: all 0.3s ease-in-out;
		background: #93b1e4;
		box-shadow: 0 10px 20px rgb(0 0 0 / 25%);

		&:hover {
			background: #7497d3;
		}
	}
}

@media (max-width: 960px) {
	.top-nav {
		.columns {
			padding: 0 10px;
		}
	}

	.button-mobile {
		display: initial;
	}

	.nav-buttons:hover,
	.menu-close:hover {
		transform: none;
	}

	.menu-button {
		opacity: 1;
		z-index: 100;
	}

	.book-button {
		opacity: 1;
		z-index: 102;
		top: auto;
		bottom: 0;
		right: 0;
		width: 100%;
		border-radius: 0;
		margin: 0;

		.fa-calendar-days {
			margin-right: 5px;
			font-size: 18px;
		}

		&.fixed {
			top: auto;
		}
	}

	.text-nav .main-nav {
		display: none;
	}
}

@media (max-width: 600px) {
	.top-nav {
		padding: 16px 0;
	}

	.menu-buttons {
		.book-button {
			padding: 10px 25px 5px;
			font-size: 16px;

			&.desktop-only {
				display: none;
			}
		}

		.menu-button {
			margin: 4px 0 0;

			.bar {
				&:first-child {
					margin: 0 0 12px;
				}

				&:last-child {
					margin: 12px 0 0;
				}
			}
		}
	}

	.menu-close {
		padding-top: 34px;
		margin-right: -18px;
	}

	.border {
		margin: 0 0 12px;
	}

	.fullscreen-nav {
		overflow-y: scroll;

		.main-nav {
			width: 100%;

			li {
				font-size: 26px;
			}

			a {
				font-size: 32px;
			}
		}

		.socials {
			margin: 25px 0;
		}

		.right-section {
			float: left;
			position: relative;
			margin: 40px 0 0;
			max-width: 100%;
		}

		.fullscreen-nav-footer {
			position: relative;
			bottom: auto;
			left: auto;
			right: auto;
			flex-flow: column wrap;
			width: 100%;
			align-items: flex-start;

			.nav-footer-menu {
				a {
					margin: 5px 25px 5px 0;
				}
			}
		}
	}

	body.locked {
		overflow-y: auto;
	}
}
</style>
